import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Landmark, Plus, RefreshCw, Search, Link as LinkIcon, X,
  ArrowUpRight, ArrowDownRight, ShieldCheck, Sparkles, Activity, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR",
      { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR",
      { day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
function fmtMoney(n, ccy = "EUR") {
  const v = (Number(n) || 0).toLocaleString("fr-FR",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${v} ${ccy === "EUR" ? "€" : ccy}`;
}
function fmtIban(iban) {
  if (!iban) return "—";
  const s = iban.replace(/\s+/g, "");
  return s.match(/.{1,4}/g)?.join(" ") || s;
}
// Formatage relatif compact ("3 min", "2 h", "hier", "12 j") pour la timeline live.
function fmtRelative(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - then) / 1000));
  if (diffSec < 60) return "à l'instant";
  if (diffSec < 3600) return `il y a ${Math.round(diffSec / 60)} min`;
  if (diffSec < 86400) return `il y a ${Math.round(diffSec / 3600)} h`;
  if (diffSec < 86400 * 2) return "hier";
  return `il y a ${Math.round(diffSec / 86400)} j`;
}

const ACCOUNT_TYPE_LABEL = {
  checking: "Courant",
  savings: "Épargne",
  card: "Carte",
  loan: "Emprunt",
  brokerage: "Titres",
  pea: "PEA",
  life_insurance: "Assurance-vie",
  unknown: "—",
};

export default function Treasury() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // filters
  const [q, setQ] = useState("");
  const [accountFilter, setAccountFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // all|unreconciled|reconciled

  // reconcile dialog
  const [reconcileTx, setReconcileTx] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  const canWrite = hasPermission("treasury.write");

  const loadAll = async () => {
    try {
      const [s, a, t] = await Promise.all([
        api.get("/banking/summary"),
        api.get("/banking/accounts"),
        api.get("/banking/transactions?limit=500"),
      ]);
      setSummary(s.data);
      setAccounts(a.data.accounts || []);
      setTransactions(t.data.transactions || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // Trigger a sync if we just came back from Bridge Connect
    if (searchParams.get("connected") === "1" && canWrite) {
      toast.info(t("treasury.connectedToast"));
      (async () => {
        setSyncing(true);
        try {
          const r = await api.post("/banking/sync");
          if (r.data?.ok) {
            toast.success(t("treasury.syncSuccess", {
              accounts: r.data.accounts_upserted || 0,
              transactions: r.data.transactions_upserted || 0,
            }));
          }
        } catch (e) {
          toast.error(apiError(e));
        } finally {
          setSyncing(false);
          await loadAll();
          // Strip the query params so refreshing doesn't re-trigger.
          const p = new URLSearchParams(searchParams);
          p.delete("connected");
          setSearchParams(p, { replace: true });
        }
      })();
    }
  }, []);

  const onConnect = async () => {
    setConnecting(true);
    try {
      const r = await api.post("/banking/connect-session",
        { account_types: "payment" });
      if (r.data?.url) {
        window.location.assign(r.data.url);
      } else {
        toast.error("URL Bridge introuvable");
        setConnecting(false);
      }
    } catch (e) {
      toast.error(apiError(e));
      setConnecting(false);
    }
  };

  const onSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/banking/sync");
      if (r.data?.ok) {
        toast.success(t("treasury.syncSuccess", {
          accounts: r.data.accounts_upserted || 0,
          transactions: r.data.transactions_upserted || 0,
        }));
      } else {
        toast.error(t("treasury.syncFailed"));
      }
      await loadAll();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSyncing(false);
    }
  };

  const [autoRunning, setAutoRunning] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [events, setEvents] = useState([]);

  const onCleanupOld = async () => {
    if (!window.confirm(
      "Nettoyer les rapprochements avec écart > 90 jours (facture ancienne matchée avec tx récente) ?\n\n" +
      "1er clic = simulation (liste seulement)\n" +
      "2e clic = vraiment défaire les rapprochements suspects."
    )) return;
    setCleaning(true);
    try {
      // Étape 1 — dry run
      const dry = await api.post("/banking/auto-match/cleanup-old?dry_run=true");
      const n = dry.data?.count || 0;
      if (n === 0) {
        toast.success("Aucun rapprochement suspect trouvé — tout est propre 🎉");
        return;
      }
      if (!window.confirm(
        `${n} rapprochement(s) suspect(s) détecté(s) (écart > 90 j).\n\n` +
        "Défaire vraiment ces liens (tx redevient non-rapprochée, facture repasse en validée/issued) ?"
      )) return;
      const real = await api.post("/banking/auto-match/cleanup-old?dry_run=false");
      toast.success(`✓ ${real.data.undone} rapprochement(s) défait(s) sur ${real.data.detected} détecté(s).`);
      await Promise.all([loadAll(), loadEvents()]);
    } catch (e) { toast.error(apiError(e)); }
    finally { setCleaning(false); }
  };

  const loadEvents = async () => {
    try {
      const r = await api.get("/banking/auto-match/events?limit=10");
      setEvents(r.data.events || []);
    } catch (e) { /* silencieux — non critique */ }
  };
  useEffect(() => { loadEvents(); }, []);

  const onRescan = async () => {
    setRescanning(true);
    try {
      const r = await api.post("/banking/auto-match/rescan");
      const s = r.data?.stats || {};
      const total = (s.auto_linked || 0) + (s.learned_applied || 0);
      if (total > 0) {
        toast.success(
          `Rescan : ${total} nouveau(x) rapprochement(s) auto (${s.scanned} tx scannées).`);
      } else {
        toast.info(
          `Rescan terminé — 0 nouveau match sur ${s.scanned} tx (${s.candidates_only || 0} candidats à confirmer).`);
      }
      await Promise.all([loadAll(), loadEvents()]);
    } catch (e) { toast.error(apiError(e)); }
    finally { setRescanning(false); }
  };

  const onAutoReconcile = async () => {
    // Rapprochement bancaire automatique : scoring + auto-link des correspondances
    // à forte confiance. Ne touche que les transactions non rapprochées.
    setAutoRunning(true);
    try {
      const r = await api.post("/banking/reconcile/auto-run");
      const s = r.data?.stats || {};
      if (s.auto_linked > 0) {
        toast.success(
          `Rapprochement auto : ${s.auto_linked} lien(s) créé(s) automatiquement ` +
          `(${s.candidates_only} candidats en attente, ${s.no_match} sans correspondance).`
        );
      } else {
        toast.info(
          `Aucune correspondance à forte confiance sur ${s.scanned} transactions. ` +
          `${s.candidates_only} candidat(s) faible à moyenne confiance à examiner.`
        );
      }
      await loadAll();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAutoRunning(false);
    }
  };

  const openReconcile = async (tx) => {
    setReconcileTx(tx);
    setLoadingCandidates(true);
    setCandidates([]);
    try {
      // Utilise l'endpoint avec scoring en priorité — fallback sur legacy si erreur.
      const r = await api.get(`/banking/transactions/${tx.id}/auto-candidates`);
      setCandidates(r.data.candidates || []);
    } catch (e) {
      try {
        const r2 = await api.get(`/banking/transactions/${tx.id}/candidates`);
        setCandidates(r2.data.candidates || []);
      } catch (e2) {
        toast.error(apiError(e2));
      }
    } finally {
      setLoadingCandidates(false);
    }
  };

  const doReconcile = async (c) => {
    if (!reconcileTx) return;
    try {
      await api.post(`/banking/transactions/${reconcileTx.id}/reconcile`,
        { invoice_kind: c.kind, invoice_id: c.invoice_id });
      // Feed the learning loop: user accepted this scored suggestion.
      // Silent — the learning happens in the background and doesn't need
      // to block the UX on failure.
      if (c.breakdown) {
        api.post("/banking/reconcile/feedback", {
          tx_id: reconcileTx.id, invoice_id: c.invoice_id,
          accepted: true, breakdown: c.breakdown,
        }).catch(() => {});
      }
      toast.success("Transaction rapprochée.");
      setReconcileTx(null);
      await loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doUnreconcile = async (tx) => {
    try {
      await api.post(`/banking/transactions/${tx.id}/unreconcile`);
      toast.success("Rapprochement défait.");
      await loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Client-side filtering (search is already server-side supported via `q`
  // but we filter locally to keep things snappy).
  const filteredTxns = useMemo(() => {
    return transactions.filter(tx => {
      if (accountFilter !== "all" && tx.account_id !== accountFilter) return false;
      if (statusFilter === "reconciled" && !tx.reconciled_with_id) return false;
      if (statusFilter === "unreconciled" && tx.reconciled_with_id) return false;
      if (q) {
        const needle = q.toLowerCase();
        const hay = `${tx.clean_description || ""} ${tx.provider_description || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [transactions, accountFilter, statusFilter, q]);

  const accountsById = useMemo(() => {
    const m = {};
    accounts.forEach(a => { m[a.id] = a; });
    return m;
  }, [accounts]);

  const totalBalance = useMemo(() => {
    const eur = (summary?.balances_by_currency || [])
      .find(b => b.currency === "EUR");
    return eur?.amount || 0;
  }, [summary]);

  const hasBank = accounts.length > 0;

  return (
    <div className="space-y-6" data-testid="treasury-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
            {t("sidebar.treasury")}
          </div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            {t("treasury.title")}
          </h1>
          <p className="mt-1 text-sm text-slate-500">{t("treasury.subtitle")}</p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCleanupOld}
                    disabled={cleaning || !hasBank}
                    title="Détecte et défait les rapprochements où l'écart facture ↔ tx dépasse 90 jours."
                    data-testid="treasury-cleanup-old-btn"
                    className="border-amber-200 text-amber-700 hover:bg-amber-50">
              <ShieldCheck className={cn("h-4 w-4 mr-1.5",
                cleaning && "animate-pulse")} strokeWidth={2} />
              {cleaning ? "Analyse…" : "Nettoyer > 90j"}
            </Button>
            <Button variant="outline" onClick={onRescan}
                    disabled={rescanning || !hasBank}
                    title="Rescanner toutes les transactions bancaires contre les factures non payées."
                    data-testid="treasury-rescan-btn">
              <Zap className={cn("h-4 w-4 mr-1.5",
                rescanning && "animate-pulse text-amber-500")} strokeWidth={2} />
              {rescanning ? "Rescan…" : "Rescanner"}
            </Button>
            <Button variant="outline" onClick={onAutoReconcile}
                    disabled={autoRunning || !hasBank}
                    data-testid="treasury-auto-reconcile-btn">
              <Sparkles className={cn("h-4 w-4 mr-1.5",
                autoRunning && "animate-pulse")} strokeWidth={2} />
              {autoRunning ? "Analyse…" : "Auto-réconciliation"}
            </Button>
            <Button variant="outline" onClick={onSync}
                    disabled={syncing || !hasBank}
                    data-testid="treasury-sync-btn">
              <RefreshCw className={cn("h-4 w-4 mr-1.5",
                syncing && "animate-spin")} strokeWidth={2} />
              {syncing ? t("treasury.syncing") : t("treasury.syncBtn")}
            </Button>
            <Button onClick={onConnect} disabled={connecting}
                    data-testid="treasury-connect-btn">
              <Plus className="h-4 w-4 mr-1.5" strokeWidth={2} />
              {connecting ? t("treasury.connecting") : t("treasury.connectBtn")}
            </Button>
          </div>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5" data-testid="treasury-kpi-balance">
          <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
            {t("treasury.summary.totalBalance")}
          </div>
          <div className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 tabular-nums">
            {fmtMoney(totalBalance)}
          </div>
        </Card>
        <Card className="p-5" data-testid="treasury-kpi-accounts">
          <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
            {t("treasury.summary.accounts")}
          </div>
          <div className="mt-2 text-3xl font-semibold tabular-nums text-slate-900">
            {summary?.account_count ?? "—"}
          </div>
        </Card>
        <Card className="p-5" data-testid="treasury-kpi-txns">
          <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
            {t("treasury.summary.txns30d")}
          </div>
          <div className="mt-2 text-3xl font-semibold tabular-nums text-slate-900">
            {summary?.transactions_last_30d ?? "—"}
          </div>
        </Card>
        <Card className="p-5 bg-slate-50" data-testid="treasury-kpi-sync">
          <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
            {summary?.last_sync_at
              ? t("treasury.lastSyncAt", { when: fmtDateTime(summary.last_sync_at) })
              : t("treasury.neverSynced")}
          </div>
          <div className="mt-2 text-xs text-slate-500 flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2} />
            {t("treasury.summary.autoSync")}
          </div>
        </Card>
      </div>

      {/* Empty state */}
      {!loading && !hasBank && (
        <Card className="p-10 text-center" data-testid="treasury-empty">
          <Landmark className="h-10 w-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-lg font-medium text-slate-900">
            {t("treasury.emptyTitle")}
          </div>
          <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
            {t("treasury.emptyDesc")}
          </p>
          {canWrite && (
            <Button className="mt-5" onClick={onConnect} disabled={connecting}
                    data-testid="treasury-empty-connect-btn">
              <Plus className="h-4 w-4 mr-1.5" strokeWidth={2} />
              {connecting ? t("treasury.connecting") : t("treasury.connectBtn")}
            </Button>
          )}
        </Card>
      )}

      {/* Timeline live rapprochement */}
      {hasBank && events.length > 0 && (
        <Card className="p-5" data-testid="treasury-live-timeline">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Activity className="h-4 w-4 text-emerald-600" strokeWidth={2} />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  Activité rapprochement — 10 derniers événements
                </div>
                <div className="text-[11px] text-slate-500">
                  Le moteur v4 travaille en arrière-plan à chaque nouvelle facture
                  et à chaque tx bancaire synchronisée.
                </div>
              </div>
            </div>
            <button onClick={loadEvents}
                    className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                    data-testid="treasury-timeline-refresh">
              <RefreshCw className="h-3 w-3" strokeWidth={2} />
              Actualiser
            </button>
          </div>
          <ul className="space-y-1.5">
            {events.map((e) => {
              const negative = (e.tx?.amount || 0) < 0;
              const inv = e.invoice;
              const isAuto = e.kind === "auto";
              return (
                <li key={e.id}
                    className="flex items-center justify-between p-2 rounded-md hover:bg-slate-50 text-xs group"
                    data-testid={`timeline-event-${e.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={cn("h-1.5 w-1.5 rounded-full shrink-0",
                                        isAuto ? "bg-emerald-500" : "bg-blue-500")} />
                    <span className={cn("font-mono tabular-nums shrink-0",
                                        negative ? "text-slate-700" : "text-emerald-700")}>
                      {fmtMoney(e.tx?.amount)}
                    </span>
                    <span className="text-slate-600 truncate" title={e.tx?.description}>
                      {e.tx?.description || "—"}
                    </span>
                    <span className="text-slate-400">→</span>
                    {inv ? (
                      <span className="text-slate-700 truncate">
                        {inv.kind === "supplier" ? "Facture" : "Facture client"}{" "}
                        <span className="font-mono text-blue-700">
                          {inv.invoice_number || "?"}
                        </span>
                        {inv.party && (
                          <span className="text-slate-500"> · {inv.party}</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-slate-400 italic">facture supprimée</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline"
                           className={cn("text-[9px]",
                             isAuto
                               ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                               : "border-blue-200 bg-blue-50 text-blue-700")}>
                      {isAuto ? "Auto" : "Manuel"}
                    </Badge>
                    <span className="text-[10px] text-slate-400 tabular-nums">
                      {fmtRelative(e.at)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {/* Accounts table */}
      {hasBank && (
        <Card className="overflow-hidden" data-testid="treasury-accounts-card">
          <div className="px-5 py-4 border-b border-slate-200">
            <div className="font-medium text-slate-900">
              {t("treasury.accounts.title")}
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("treasury.accounts.col.name")}</TableHead>
                <TableHead>{t("treasury.accounts.col.type")}</TableHead>
                <TableHead className="text-right">{t("treasury.accounts.col.balance")}</TableHead>
                <TableHead className="hidden md:table-cell">{t("treasury.accounts.col.iban")}</TableHead>
                <TableHead className="hidden lg:table-cell">{t("treasury.accounts.col.lastRefresh")}</TableHead>
                <TableHead>{t("treasury.accounts.col.status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map(a => (
                <TableRow key={a.id} data-testid={`account-row-${a.id}`}>
                  <TableCell className="font-medium">{a.name || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {ACCOUNT_TYPE_LABEL[a.type] || a.type || "—"}
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">
                    {fmtMoney(a.balance, a.currency)}
                  </TableCell>
                  <TableCell className="hidden md:table-cell font-mono text-xs text-slate-600">
                    {fmtIban(a.iban)}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-slate-500">
                    {fmtDateTime(a.last_refresh_at)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline"
                           className={cn("text-[10px]",
                             a.last_refresh_status === "successful"
                               ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                               : a.last_refresh_status === "failed"
                                 ? "border-red-200 bg-red-50 text-red-700"
                                 : "border-amber-200 bg-amber-50 text-amber-700")}>
                      {t(`treasury.accounts.refreshStatus.${a.last_refresh_status || "pending"}`,
                         a.last_refresh_status || "—")}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Transactions */}
      {hasBank && (
        <Card className="overflow-hidden" data-testid="treasury-transactions-card">
          <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
            <div className="font-medium text-slate-900">
              {t("treasury.transactions.title")}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                id="tx-account-filter"
                name="tx-account-filter"
                data-testid="tx-account-filter"
                className="h-9 rounded-md border border-slate-200 text-sm px-2 bg-white"
                value={accountFilter}
                onChange={(e) => setAccountFilter(e.target.value)}>
                <option value="all">{t("treasury.transactions.filters.accountAll")}</option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              <Tabs value={statusFilter} onValueChange={setStatusFilter}>
                <TabsList className="h-9">
                  <TabsTrigger value="all" data-testid="tx-filter-all">
                    {t("treasury.transactions.filters.all")}
                  </TabsTrigger>
                  <TabsTrigger value="unreconciled" data-testid="tx-filter-unreconciled">
                    {t("treasury.transactions.filters.unreconciled")}
                  </TabsTrigger>
                  <TabsTrigger value="reconciled" data-testid="tx-filter-reconciled">
                    {t("treasury.transactions.filters.reconciled")}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={2} />
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                       data-testid="tx-search"
                       placeholder={t("treasury.transactions.filters.search")}
                       className="pl-8 h-9 w-64" />
              </div>
            </div>
          </div>
          {filteredTxns.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500" data-testid="tx-empty">
              {t("treasury.transactions.none")}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">{t("treasury.transactions.col.date")}</TableHead>
                  <TableHead>{t("treasury.transactions.col.description")}</TableHead>
                  <TableHead className="text-right w-32">{t("treasury.transactions.col.amount")}</TableHead>
                  <TableHead className="w-64">{t("treasury.transactions.col.reconciliation")}</TableHead>
                  <TableHead className="w-32 text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTxns.slice(0, 300).map(tx => {
                  const negative = (tx.amount || 0) < 0;
                  const acct = accountsById[tx.account_id];
                  return (
                    <TableRow key={tx.id} data-testid={`tx-row-${tx.id}`}>
                      <TableCell className="text-xs tabular-nums text-slate-600">
                        {fmtDate(tx.date)}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-slate-900">
                          {tx.clean_description || tx.provider_description || "—"}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap mt-0.5">
                          {acct && (
                            <span className="text-[10px] text-slate-400">{acct.name}</span>
                          )}
                          {tx.is_rem_sdd && tx.remise_sepa_id && tx.remise_total_count > 0 && (
                            <span
                              data-testid={`tx-rem-sdd-progress-${tx.id}`}
                              className={cn(
                                "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium cursor-pointer hover:opacity-80",
                                tx.remise_matched_count >= tx.remise_total_count
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-amber-100 text-amber-800"
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`/app/reconciliation?tab=remises`, "_self");
                              }}
                              title="Ouvrir la remise SEPA">
                              REM SDD · {tx.remise_matched_count}/{tx.remise_total_count} rapprochées
                            </span>
                          )}
                          {tx.is_rem_sdd && !tx.remise_sepa_id && (
                            <span
                              data-testid={`tx-rem-sdd-no-import-${tx.id}`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800 cursor-pointer hover:opacity-80"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`/app/reconciliation?tab=remises`, "_self");
                              }}
                              title="Cette ligne agrège plusieurs prélèvements clients — importez le PDF de la remise pour la décomposer.">
                              REM SDD · importer PDF
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className={cn(
                        "text-right font-medium tabular-nums text-sm",
                        negative ? "text-slate-900" : "text-emerald-700")}>
                        <span className="inline-flex items-center gap-1">
                          {negative
                            ? <ArrowDownRight className="h-3.5 w-3.5" strokeWidth={2} />
                            : <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />}
                          {fmtMoney(tx.amount, tx.currency)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {tx.reconciled_with_id ? (
                          <Badge variant="outline"
                                 className="border-blue-200 bg-blue-50 text-blue-700 text-[10px]">
                            <LinkIcon className="h-3 w-3 mr-1" strokeWidth={2} />
                            {tx.reconciled_with_kind === "customer"
                              ? "Facture client"
                              : "Facture fournisseur"}
                          </Badge>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {canWrite && (
                          tx.reconciled_with_id ? (
                            <Button variant="ghost" size="sm"
                                    onClick={() => doUnreconcile(tx)}
                                    data-testid={`tx-unreconcile-${tx.id}`}
                                    className="h-7 text-xs">
                              <X className="h-3 w-3 mr-1" strokeWidth={2} />
                              {t("treasury.transactions.unreconcileBtn")}
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm"
                                    onClick={() => openReconcile(tx)}
                                    data-testid={`tx-reconcile-${tx.id}`}
                                    className="h-7 text-xs">
                              <LinkIcon className="h-3 w-3 mr-1" strokeWidth={2} />
                              {t("treasury.transactions.reconcileBtn")}
                            </Button>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      {/* Reconcile dialog */}
      <Dialog open={!!reconcileTx} onOpenChange={(v) => !v && setReconcileTx(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("treasury.transactions.suggestions.title")}</DialogTitle>
            <DialogDescription>
              {t("treasury.transactions.suggestions.desc")}
            </DialogDescription>
          </DialogHeader>
          {reconcileTx && (
            <div className="rounded-md border border-slate-200 p-3 bg-slate-50 text-sm">
              <div className="text-xs text-slate-500">Transaction</div>
              <div className="flex items-center justify-between mt-1">
                <div>
                  <div className="font-medium">
                    {reconcileTx.clean_description || reconcileTx.provider_description}
                  </div>
                  <div className="text-xs text-slate-500">{fmtDate(reconcileTx.date)}</div>
                </div>
                <div className={cn("font-medium tabular-nums",
                  (reconcileTx.amount || 0) < 0 ? "text-slate-900" : "text-emerald-700")}>
                  {fmtMoney(reconcileTx.amount, reconcileTx.currency)}
                </div>
              </div>
            </div>
          )}
          <div className="max-h-80 overflow-auto -mx-1">
            {loadingCandidates ? (
              <div className="py-8 text-center text-sm text-slate-500">
                {t("common.loading")}
              </div>
            ) : candidates.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500"
                   data-testid="candidates-empty">
                {t("treasury.transactions.suggestions.none")}
              </div>
            ) : (
              <div className="space-y-2 px-1">
                {candidates.map((c, i) => (
                  <div key={i}
                       className="rounded-md border border-slate-200 p-3 flex items-center justify-between hover:border-slate-300 transition-colors"
                       data-testid={`candidate-${i}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">
                          {t(`treasury.transactions.suggestions.kind.${c.kind}`)}
                        </Badge>
                        {typeof c.score === "number" && (
                          <Badge
                            variant={c.score >= 0.92 ? "default" : "secondary"}
                            className="text-[10px]"
                            data-testid={`candidate-score-${i}`}
                            title={c.breakdown
                              ? `Montant ${(c.breakdown.amount * 100).toFixed(0)}% · `
                                + `Date ${(c.breakdown.date * 100).toFixed(0)}% · `
                                + `Libellé ${(c.breakdown.label * 100).toFixed(0)}% · `
                                + `Bénéficiaire ${(c.breakdown.party * 100).toFixed(0)}%`
                              : undefined}>
                            {(c.score * 100).toFixed(0)}%
                          </Badge>
                        )}
                        <span className="font-medium text-sm truncate">
                          {c.invoice_number || "—"} · {c.party_name || "—"}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {fmtDate(c.due_date || c.invoice_date)} · {fmtMoney(c.total_ttc)}
                      </div>
                    </div>
                    <Button size="sm" onClick={() => doReconcile(c)}
                            data-testid={`candidate-select-${i}`}>
                      {t("treasury.transactions.suggestions.select")}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReconcileTx(null)}>
              {t("common.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
